import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, ArrowRight, DollarSign, TrendingUp, Bot,
  Landmark, Users, Handshake, MapPin, Calendar
} from 'lucide-react';
import './VegasMastermindPage.css';

const CHECKOUT_URL = 'https://www.commas.com/checkout/PrXn2DEVOKISftFA';

const BENEFITS = [
  { icon: DollarSign,  emoji: '💰', text: 'Build a monthly recurring revenue business' },
  { icon: TrendingUp,  emoji: '📈', text: 'Generate and monetize more leads' },
  { icon: Bot,         emoji: '🤖', text: 'Make money with AI and marketing' },
  { icon: Landmark,    emoji: '🏦', text: 'Earn commissions in the business loan industry' },
  { icon: Users,       emoji: '🦈', text: 'Partner with Kevin Harrington and our Shark team' },
  { icon: Handshake,   emoji: '🤝', text: 'Build relationships with entrepreneurs who are actively making things happen' },
];

const VegasMastermindPage = () => {
  useEffect(() => {
    document.title = 'Vegas Mastermind · The Collins Academy';
  }, []);

  const track = (event, props = {}) => {
    try {
      if (typeof window !== 'undefined' && window.posthog?.capture) {
        window.posthog.capture(event, props);
      }
    } catch { /* no-op */ }
  };

  const handleRegisterClick = () => {
    track('vegas_mastermind_register_clicked', { destination: 'commas_checkout' });
  };

  return (
    <div className="vm-page" data-testid="vegas-mastermind-page">

      {/* ============ HERO ============ */}
      <section className="vm-hero" data-testid="vm-hero">
        <div className="vm-hero-bg" aria-hidden="true">
          <div className="vm-hero-glow vm-hero-glow-a" />
          <div className="vm-hero-glow vm-hero-glow-b" />
          <div className="vm-hero-grid" />
        </div>

        <motion.div
          className="vm-hero-inner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="vm-eyebrow" data-testid="vm-eyebrow">
            <Sparkles size={13} strokeWidth={2.4} />
            EXCLUSIVE MASTERMIND EVENT
            <Sparkles size={13} strokeWidth={2.4} />
          </span>

          <h1 className="vm-title" data-testid="vm-title">
            <span className="vm-title-line-1">Vegas</span>
            <span className="vm-title-line-2">Mastermind</span>
          </h1>

          <div className="vm-hero-meta">
            <span><MapPin size={14} strokeWidth={2.4} /> LAS VEGAS · NEVADA</span>
            <span className="vm-hero-meta-divider" aria-hidden="true">·</span>
            <span><Calendar size={14} strokeWidth={2.4} /> LIMITED SEATING</span>
          </div>

          <p className="vm-hero-sub">
            Environment matters. Get in the room with entrepreneurs who are
            actively making things happen — and start thinking bigger.
          </p>

          <a
            href={CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="vm-cta vm-cta-hero"
            data-testid="vm-register-cta-hero"
            onClick={handleRegisterClick}
          >
            <span>REGISTER FOR VEGAS MASTERMIND</span>
            <ArrowRight size={20} strokeWidth={2.4} />
          </a>
        </motion.div>
      </section>

      {/* ============ STORY ============ */}
      <section className="vm-story" data-testid="vm-story">
        <motion.div
          className="vm-story-inner"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="vm-section-eyebrow">THE FORT LAUDERDALE PLAYBOOK</div>
          <h2 className="vm-h2">
            We kicked off with a <em>137-foot yacht</em>.
          </h2>

          <div className="vm-body-copy">
            <p>
              We kicked off our Fort Lauderdale Mastermind with a 4 hour cruise on
              a <strong>137 foot yacht</strong>. Why? Because your environment matters.
            </p>
            <p>
              When you put yourself around successful entrepreneurs, big ideas
              and a wealthy lifestyle, you start thinking differently about what
              is possible.
            </p>
            <p>
              The yacht cruise was also the perfect way to break the ice, start
              building relationships and create opportunities before the event
              begins.
            </p>
            <p className="vm-emphasis">
              That is exactly what we are bringing to our Vegas Mastermind.
            </p>
          </div>
        </motion.div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section className="vm-benefits" data-testid="vm-benefits">
        <motion.div
          className="vm-benefits-inner"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="vm-section-eyebrow">WHAT YOU WILL LEARN</div>
          <h2 className="vm-h2">You will learn how to <em>build</em>.</h2>

          <div className="vm-benefit-grid">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div
                  key={i}
                  className="vm-benefit-card"
                  data-testid={`vm-benefit-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: 'easeOut' }}
                >
                  <div className="vm-benefit-icon">
                    <Icon size={22} strokeWidth={2} />
                  </div>
                  <div className="vm-benefit-text">
                    <span className="vm-benefit-emoji" aria-hidden="true">{b.emoji}</span>
                    <span>{b.text}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* ============ CLOSING PITCH ============ */}
      <section className="vm-closing" data-testid="vm-closing">
        <motion.div
          className="vm-closing-inner"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="vm-body-copy vm-body-center">
            <p>
              The speakers and strategies are valuable, but the
              <span className="vm-gold"> right conversation or partnership</span> could
              be worth even more.
            </p>
            <p>
              We do not just want to motivate you for a weekend. We want to help
              you build <strong>predictable monthly income</strong>, create more
              freedom and live life on your own terms.
            </p>
            <p className="vm-tagline">
              Come to Vegas, get in the right environment and <em>start thinking bigger</em>.
            </p>
          </div>

          <a
            href={CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="vm-cta vm-cta-closing"
            data-testid="vm-register-cta-closing"
            onClick={handleRegisterClick}
          >
            <span>REGISTER FOR VEGAS MASTERMIND</span>
            <ArrowRight size={20} strokeWidth={2.4} />
          </a>

          <p className="vm-seat-note">Limited seating · Secure yours today</p>
        </motion.div>
      </section>
    </div>
  );
};

export default VegasMastermindPage;
